import { spacesField, spacesMatchField } from '../collections/spaces';
import { COMMONAREA_TYPE_OPTION } from './constants';
import {
  explicitInOrUndefined,
  inOrUndefined,
} from './shared-pipeline-patterns';
import type { MongoExpr } from './types';

/** A tag usage's period relative to the report date. */
export const TAG_USAGE_PERIODS = ['active', 'past', 'upcoming'] as const;
export type TagUsagePeriod = (typeof TAG_USAGE_PERIODS)[number];

/** The filter fields every spaces-collection report supports. */
export interface SpacesBaseFilters {
  objectTypes?: string[];
  realestateIds?: string[];
  companyIds?: string[];
  communityIds?: string[];
  /** Region entity (customer-defined grouping) — UI label Region. */
  regionIds?: string[];
  structureIds?: string[];
  /** Spaces linked to these tenants via any contract (e.g. the vacater). */
  tenantIds?: string[];
  /** Ort (UI label; was briefly Region) — distinct address.city values. */
  cities?: string[];
  /** Kategori — raw space.category codes (PX_*). */
  categories?: string[];
  roomCounts?: number[];
  usableAreaMin?: number;
  usableAreaMax?: number;
  floors?: number[];
  /** Spaces with a usage of any of these tags… */
  tagIds?: string[];
  /** …in one of these periods (default: active on the report date). */
  tagPeriods?: TagUsagePeriod[];
}

/**
 * Tag usages carry optional start/end dates; each period is a usage
 * condition relative to `dt` (the report's as-of instant). A null bound is
 * open, so a dateless usage is always active. Inclusive like
 * `applicableAtExpr`: a usage ending on `dt` is still active.
 */
function tagPeriodMatch(period: TagUsagePeriod, dt: string) {
  switch (period) {
    case 'active':
      return {
        $and: [
          { $or: [{ start_date: null }, { start_date: { $lte: dt } }] },
          { $or: [{ end_date: null }, { end_date: { $gte: dt } }] },
        ],
      };
    case 'past':
      return { end_date: { $lt: dt } };
    case 'upcoming':
      return { start_date: { $gt: dt } };
  }
}

/** `$elemMatch` on `tag_usages`: one usage must have the tag AND the period. */
function tagUsagesMatch(filters: SpacesBaseFilters, dt: string) {
  if (!filters.tagIds || filters.tagIds.length === 0) return undefined;
  const periods = filters.tagPeriods?.length
    ? filters.tagPeriods
    : (['active'] as const);
  return {
    $elemMatch: {
      tag_id: { $in: filters.tagIds },
      // Every period = any usage, no date condition needed.
      ...(TAG_USAGE_PERIODS.some((period) => !periods.includes(period)) && {
        $or: periods.map((period) => tagPeriodMatch(period, dt)),
      }),
    },
  };
}

/**
 * The additionalMatch/preMatch routing shared by the three spaces reports
 * (objects, rentroll, vacancies). Each report's buildXFilterParts spreads
 * this and adds its own postMatch — a base filter added here lands in all
 * three reports at once, so the near-identical blocks can never drift
 * (the vacancies no-op tenant filter pitfall).
 */
export function buildSpacesBaseFilterParts(
  filters: SpacesBaseFilters,
  /** The report's as-of instant — the tag periods are relative to it. */
  dt: string,
  allowCommonareas = false
): {
  additionalMatch: Record<string, MongoExpr | undefined>;
  preMatch: Record<string, MongoExpr | undefined>;
} {
  return {
    // Every non-list-usage field goes to the indexed pre-filter — it runs
    // before server pagination and the permission projection, and keeping
    // the pipeline free of $match is what lets page requests use
    // server-side pagination (see buildPaginatedRequest).
    additionalMatch: {
      [spacesMatchField('structure.realestate_id')]: explicitInOrUndefined(
        filters.realestateIds
      ),
      [spacesMatchField('landlord.id')]: inOrUndefined(filters.companyIds),
      [spacesMatchField('structure.id')]: inOrUndefined(filters.structureIds),
      [spacesMatchField('space.ct_identifier')]: filters.objectTypes
        ? {
            $in: allowCommonareas
              ? filters.objectTypes
              : filters.objectTypes.filter(
                  (type) => type !== COMMONAREA_TYPE_OPTION.value
                ),
          }
        : { $ne: COMMONAREA_TYPE_OPTION.value },
      [spacesMatchField('realestate.community_id')]: inOrUndefined(
        filters.communityIds
      ),
      [spacesMatchField('region.id')]: inOrUndefined(filters.regionIds),
      [spacesMatchField('address.city')]: inOrUndefined(filters.cities),
      [spacesMatchField('space.category')]: inOrUndefined(filters.categories),
      [spacesMatchField('space.room_count')]: inOrUndefined(filters.roomCounts),
      [spacesMatchField('space.usable_area')]:
        filters.usableAreaMin != null || filters.usableAreaMax != null
          ? {
              ...(filters.usableAreaMin != null && {
                $gte: filters.usableAreaMin,
              }),
              ...(filters.usableAreaMax != null && {
                $lte: filters.usableAreaMax,
              }),
            }
          : undefined,
      // Array-VALUED field on the scalar `space` usage — accepted by the
      // backend pre-filter (only list USAGES are rejected); multikey $in
      // matches units spanning the selected floor, as before.
      [spacesMatchField('space.floors')]: inOrUndefined(filters.floors),
    },
    // List-usage paths are rejected by additional_match_stage — they must
    // stay a pipeline $match (which forces legacy pagination on the page
    // request).
    preMatch: {
      [spacesField('contracts.tenant_id')]: inOrUndefined(filters.tenantIds),
      [spacesField('tag_usages')]: tagUsagesMatch(filters, dt),
    },
  };
}
